import React from 'react';
import { motion } from 'framer-motion';
import { Mail, Phone, MapPin, Users, Mic, Dna, PawPrint } from 'lucide-react';
import { Link } from 'react-router-dom';
import { useTheme } from '../contexts/ThemeContext';
import { contactInfo } from '../config/contactInfo';

const Footer: React.FC = () => {
  const { theme } = useTheme();

  const productLinks = [
    { name: 'StaffAxis', href: '/products', icon: Users },
    { name: 'NoteAxis', href: '/products', icon: Mic },
    { name: 'GenAxis', href: '/products', icon: Dna },
    { name: 'VetAxis', href: '/products', icon: PawPrint },
  ];

  const companyLinks = [
    { name: 'About Us', href: '/about' },
    { name: 'Blog', href: '/blog' },
    { name: 'Support', href: '/support' },
    { name: 'Contact', href: '/contact' },
  ];

  const legalLinks = [
    { name: 'Privacy Policy', href: '/privacy' },
    { name: 'Terms of Service', href: '/terms' },
    { name: 'POPIA Compliance', href: '/popia' },
    { name: 'Cookie Policy', href: '/cookies' },
    { name: 'Acceptable Use', href: '/acceptable-use' },
    { name: 'Refund Policy', href: '/refund-policy' },
  ];

  const contactInfoDisplay = [
    { icon: Mail, text: contactInfo.email.primary },
    { icon: Phone, text: contactInfo.phone.display },
    { icon: MapPin, text: contactInfo.business.location },
  ];

  return (
    <footer id="contact" className="bg-primary-50 dark:bg-dark-900">
      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-12">
          {/* Col 1: Logo + Tagline + Contact */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
          >
            <div className="flex items-center gap-2 mb-4">
              <img
                src={theme === 'light' ? '/bioaxis-logo-colour.png' : '/bioaxis-logo.png'}
                alt="BioAxis Logo"
                className="w-7 h-7 rounded-lg"
              />
              <span className="text-lg font-bold text-primary-600 dark:text-primary-400">BioAxis</span>
            </div>
            <p className="text-dark-500 dark:text-dark-400 leading-relaxed text-sm mb-4">
              Smart software solutions for healthcare professionals worldwide.
            </p>
            <div className="space-y-2">
              {contactInfoDisplay.map((contact, index) => (
                <div key={index} className="flex items-center gap-2.5 text-dark-500 dark:text-dark-400 text-sm">
                  <contact.icon className="w-4 h-4 text-primary-500 flex-shrink-0" />
                  <span>{contact.text}</span>
                </div>
              ))}
            </div>
          </motion.div>

          {/* Col 2: Products */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6, delay: 0.1 }}
          >
            <h4 className="font-display text-sm font-semibold text-dark-800 dark:text-dark-200 mb-3 uppercase tracking-wide">Products</h4>
            <ul className="space-y-2">
              {productLinks.map((link, index) => (
                <li key={index}>
                  <Link
                    to={link.href}
                    className="text-dark-500 dark:text-dark-400 hover:text-secondary-500 dark:hover:text-secondary-400 transition-colors duration-200 flex items-center gap-1.5 text-sm"
                  >
                    <link.icon className="w-3.5 h-3.5 text-primary-500 flex-shrink-0" />
                    {link.name}
                  </Link>
                </li>
              ))}
            </ul>
            <h4 className="font-display text-sm font-semibold text-dark-800 dark:text-dark-200 mb-3 mt-6 uppercase tracking-wide">Company</h4>
            <ul className="space-y-2">
              {companyLinks.map((link, index) => (
                <li key={index}>
                  <Link
                    to={link.href}
                    className="text-dark-500 dark:text-dark-400 hover:text-secondary-500 dark:hover:text-secondary-400 transition-colors duration-200 text-sm"
                  >
                    {link.name}
                  </Link>
                </li>
              ))}
            </ul>
          </motion.div>

          {/* Col 3: Legal */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6, delay: 0.2 }}
          >
            <h4 className="font-display text-sm font-semibold text-dark-800 dark:text-dark-200 mb-3 uppercase tracking-wide">Legal</h4>
            <ul className="space-y-2">
              {legalLinks.map((link, index) => (
                <li key={index}>
                  <Link
                    to={link.href}
                    className="text-dark-500 dark:text-dark-400 hover:text-secondary-500 dark:hover:text-secondary-400 transition-colors duration-200 text-sm"
                  >
                    {link.name}
                  </Link>
                </li>
              ))}
            </ul>
          </motion.div>

          {/* Col 4: Contact */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6, delay: 0.3 }}
            className="md:hidden lg:block"
          >
            <h4 className="font-display text-sm font-semibold text-dark-800 dark:text-dark-200 mb-3 uppercase tracking-wide">Contact</h4>
            <div className="space-y-3">
              {contactInfoDisplay.map((contact, index) => (
                <div key={index} className="flex items-center gap-2.5 text-dark-500 dark:text-dark-400 text-sm">
                  <contact.icon className="w-4 h-4 text-primary-500 flex-shrink-0" />
                  <span>{contact.text}</span>
                </div>
              ))}
            </div>
          </motion.div>
        </div>

        {/* Copyright */}
        <motion.div
          className="border-t border-primary-200/30 dark:border-primary-900/20 mt-12 pt-8 text-center"
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6, delay: 0.3 }}
        >
          <p className="text-dark-400 dark:text-dark-500 text-sm">
            &copy; {new Date().getFullYear()} BioAxis. All rights reserved.
          </p>
        </motion.div>
      </div>
    </footer>
  );
};

export default Footer;
